<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        ></v-progress-circular>
        <v-card v-if="!loading" max-width="650px">
          <v-card-title>
            <span class="headline">
              {{ $t("builder.models.details.preview") }}</span
            >
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn text color="primary" @click="edit()"
                ><v-icon small data-cy="modelDetailsEditBtn" class="mx-2"
                  >mdi-pencil</v-icon
                >{{ $t("builder.models.details.edit") }}</v-btn
              >
              <v-btn text color="red" @click="deleteFn()">
                <v-icon small data-cy="modelDetailsDeleteBtn" class="mx-2"
                  >mdi-delete</v-icon
                >{{ $t("builder.models.details.delete") }}</v-btn
              >
            </v-card-actions>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.brand")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{ getFullGroupName }}
                      <v-chip class="ml-2" outlined>{{
                        getGroupType(editedItem.brand)
                      }}</v-chip></v-card-subtitle
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.model")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.model }}</v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.type")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{ editedItem.typeName }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>

                <v-col
                  cols="12"
                  sm="12"
                  md="12"
                  lg="12"
                  v-if="showIndustrialCode"
                >
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.industrialCode")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{ editedItem.industrial_code }}
                    </v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.telematicsProfile")
                    }}</v-card-title>
                    <v-card-subtitle v-if="editedItem.selected_profile">
                      {{ editedItem.selected_profile.name }}</v-card-subtitle
                    >
                    <v-card-subtitle v-if="!editedItem.selected_profile">
                      {{ $t("builder.models.details.noProfile") }}
                    </v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">
                      {{ $t("builder.models.details.mobileBackgroundImage") }}
                    </v-card-title>
                    <v-card-subtitle
                      v-if="!editedItem.mobile_image_file_resource"
                    >
                      {{ $t("builder.models.details.noImage") }}
                    </v-card-subtitle>
                  </v-card>
                  <direct-file-upload
                    v-if="editedItem.mobile_image_file_resource"
                    :details-view="true"
                    asset-type="image_asset"
                    :files="
                      editedItem.mobile_image_file_resource !== null &&
                      Object.keys(editedItem.mobile_image_file_resource)
                        .length !== 0
                        ? [editedItem.mobile_image_file_resource]
                        : []
                    "
                  >
                    <template v-slot:default="{ file }">
                      <v-menu v-model="menu" offset-x>
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            style="cursor: pointer; position: absolute"
                            v-bind="attrs"
                            v-on="on"
                            @mouseover="menu = true"
                            @mouseleave="menu = false"
                          >
                            <v-icon class="mr-2">mdi-file-image</v-icon>
                            {{ file.name }}
                          </div>
                        </template>

                        <v-card class="pa-2">
                          <v-list>
                            <v-list-item>
                              <v-list-item-content>
                                <v-img
                                  max-height="150"
                                  max-width="250"
                                  :src="
                                    typeof file.src === 'undefined'
                                      ? getResizedImageSrc(file.link)
                                      : file.src
                                  "
                                ></v-img>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-card>
                      </v-menu>
                    </template>
                  </direct-file-upload>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.description")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.comment }}</v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.parts")
                    }}</v-card-title>
                    <v-card-subtitle>
                      <item-picker
                        :items="editedItem.items"
                        :preview="true"
                        :readonly="true"
                        feature="template"
                      />
                    </v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.models.details.documents")
                    }}</v-card-title>
                    <v-card-subtitle>
                      <document-picker
                        :documents="editedItem.documents"
                        :preview="true"
                        :unavailable-documents="[]"
                        :readonly="true"
                        feature="template"
                      />
                    </v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">
                      {{ $t("builder.models.details.advanced") }}
                    </v-card-title>
                    <v-card-title class="subtitle-1 mt-0 pt-0"
                      >{{ $t("builder.models.details.installationOptions") }}
                    </v-card-title>
                    <v-card-text
                      >{{
                        $t(
                          "builder.models.details.theFollowingOptionsApplyOnlyToInFactoryInstallations"
                        )
                      }}
                    </v-card-text>
                    <v-card-subtitle class="mt-0 pt-0">
                      <v-checkbox
                        class="mt-0"
                        color="primary"
                        v-model="editedItem.diag_skip_fimrware_update"
                        :label="$t('builder.models.details.skipFirmwareUpdate')"
                        disabled
                      ></v-checkbox>
                      <v-checkbox
                        class="mt-0"
                        color="primary"
                        v-model="editedItem.diag_skip_nmea"
                        :label="
                          $t('builder.models.details.skipNMEANetworkScan')
                        "
                        disabled
                      ></v-checkbox>
                      <v-card-text class="mt-0 pt-0 pb-2 pl-1 ml-0">
                        {{
                          $t(
                            "builder.models.details.onlyInProductionModelsAreAvailableToFactoryInstallers"
                          )
                        }}
                      </v-card-text>
                      <v-checkbox
                        class="mt-0"
                        color="primary"
                        v-model="editedItem.is_in_production"
                        :label="$t('builder.models.details.inProduction')"
                        disabled
                      ></v-checkbox>
                    </v-card-subtitle>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :to="{}"
              color="primary"
              data-cy="modelDetailsCancelBtn"
              text
              @click="close"
              >{{ $t("builder.models.details.cancel") }}</v-btn
            >
          </v-card-actions>
        </v-card>
        <delete-dialog
          :dialog="deleteDialog"
          :item="modelName"
          @agree="remove"
          v-show="false"
        />
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
import DocumentPicker from "@/components/pickers/DocumentPicker";
import ItemPicker from "@/components/pickers/ItemPicker";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import api from "@/utils/api.js";
import restrictions from "@/utils/restrictions";
import DirectFileUpload from "@/components/miscellaneous/DirectFileUpload.vue";

export default {
  components: { DocumentPicker, ItemPicker, DeleteDialog, DirectFileUpload },
  data: () => ({
    loading: true,
    editedItem: {
      brand: "",
      model: "",
      type: "",
      typeName: "",
      comment: "",
      documents: [],
      items: [],
      crm_group: null,
    },
    deleteDialog: false,
    modelName: null,
    menu: false,
  }),

  created() {
    api
      .getModel(this.$route.params.id)
      .then((resp) => {
        this.editedItem = resp.data;
        return this.editedItem;
      })
      .then((editedItem) => {
        return api.modelTypeAutocomplete("").then((resp) => {
          resp.data.forEach((modelType) => {
            if (modelType.uuid === editedItem.type) {
              editedItem.typeName = modelType.name;
            }
          });

          this.loading = false;
        });
      });
  },
  computed: {
    showIndustrialCode() {
      return !!restrictions.canUseCrmFeature("has_industrial_codes");
    },
    getFullGroupName() {
      if (typeof this.editedItem.brand === "undefined") {
        return this.editedItem.brand_name;
      }

      if (this.editedItem.brand.is_range) {
        return `${this.editedItem.brand_name} - ${this.editedItem.brand.name}`;
      }

      return this.editedItem.brand_name;
    },
  },
  methods: {
    getResizedImageSrc(src) {
      const extansion =
        src.substring(src.lastIndexOf(".") + 1, src.length) || src;

      return `${src.split(extansion)[0]}250x150.${extansion}`;
    },
    getGroupType(item) {
      if (item.is_supergroup)
        return this.$t("builder.models.details.organization");
      if (item.is_range) return this.$t("builder.models.details.range");

      return this.$t("builder.models.details.brand");
    },
    close() {
      this.$router.push({
        name: "models",
      });
    },
    edit() {
      this.$router.push({
        name: "modelsEdit",
        params: { id: this.$route.params.id },
      });
    },
    deleteFn() {
      this.deleteDialog = true;
      this.modelName = this.editedItem.brand_name + " " + this.editedItem.model;
    },

    remove(agreed) {
      if (agreed) {
        api.deleteModel(this.editedItem.uuid).then((resp) => {
          this.$router.push({
            name: "models",
          });
        });
      }
      this.deleteDialog = false;
    },
  },
};
</script>
